/**
 * In a store app, asks the backend for the oldest build it still serves
 * (GET /api/chouette/app-config) and, when this build is older, covers the
 * app with a screen asking for an update. Anything going wrong (no network,
 * a backend without the route) lets the app run: only a clear "too old"
 * stops it (appBuild.fetchTooOld). Renders nothing in a browser.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../lib/api";
import { Logo } from "../components/Logo";
import { MutedText } from "../components/ui";
import { appBuild, fetchTooOld, STORE_URL, type AppBuild } from "./appBuild";

export default function UpdateGate() {
  const { t } = useTranslation();
  const [tooOld, setTooOld] = useState<AppBuild | null>(null);

  useEffect(() => {
    const app = appBuild();
    if (!app) return;
    let live = true;
    void fetchTooOld(app, API_BASE).then((old) => {
      if (live && old) setTooOld(app);
    });
    return () => {
      live = false;
    };
  }, []);

  if (!tooOld) return null;
  const store = STORE_URL[tooOld.platform];
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-title"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-night px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] text-center"
    >
      <Logo size={56} />
      <h1 id="update-title" className="headline text-2xl">{t("update.title")}</h1>
      <MutedText className="max-w-sm">{t("update.body")}</MutedText>
      {store && (
        <a href={store} className="tap-target inline-flex items-center justify-center rounded-card bg-sun px-8 py-3 font-heading font-semibold text-mint-ink overprint ink-coral">
          {t("update.button")}
        </a>
      )}
      <p className="text-violet-soft text-xs">{t("update.version", { version: tooOld.version, build: tooOld.build })}</p>
    </div>
  );
}
